import type { SessionService } from '@efdigital/ll-addon-auth';
import type {
  MediaImage,
  MediaImageSizeContent,
} from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import Service, { service } from '@ember/service';
import type { Store } from '@warp-drive/core';
import type { FindRecordOptions } from '@warp-drive/core/types/request';
import { findRecord } from '@warp-drive/utilities/json-api';

import { buildEnsureImageSizeRequest } from '../data/builders/content/media-images.ts';
import { jsonApiOptions } from '../lib/json-api.ts';
import { MediaImageInfo } from '../lib/media/media-image-info.ts';
import { getKeyFromUrl } from '../lib/media/media-image-key.ts';
import {
  MEDIA_IMAGE_TYPE,
  MediaImagePath,
} from '../lib/media/media-image-path.ts';
import type { MediaImageSizeType } from '../lib/media/models';
import { type SizePreset, Sizing, Thumbnail } from '../lib/media/sizes.ts';

const MAX_RETRIES = 60;
const POLL_DELAY = 1000;

export default class MediaImagesService extends Service {
  @service declare store: Store;
  @service declare session: SessionService;

  lookupMediaImageInfo(uri: string): MediaImageInfo | null {
    try {
      if (!uri) {
        return null;
      }

      const key = getKeyFromUrl(uri);
      const mediaImagePath = MediaImagePath.fromKey(key);
      if (mediaImagePath.type !== MEDIA_IMAGE_TYPE) {
        return null;
      }
      return new MediaImageInfo(mediaImagePath, this.store);
    } catch (error) {
      console.error('Error looking up media image info', error);
      return null;
    }
  }

  /**
   * Ensures the image has the given size.
   * @param image The media-image record.
   * @param size The size of the image. See app/lib/media/models.ts for possible values.
   * @param customSize The custom size of the image (if size is 'custom').
   * @returns Promise
   */
  async ensureSize(
    image: MediaImage,
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    size: MediaImageSizeType | string,
    customSize?: SizePreset
  ): Promise<MediaImageSizeContent | undefined> {
    // If the size is 'original' or 'thumbnail', we don't need to do anything
    // The original image is always available and the thumbnail is generated automatically
    if (size === 'original') {
      console.log('returning original');
      return {
        url: image.original?.url,
        width: image.original?.width,
        height: image.original?.height,
        size: 'original',
      };
    } else if (size === 'thumbnail') {
      console.log('returning thumbnail');
      return {
        url: image.thumbnailUrl,
        width: Thumbnail.width,
        height: Thumbnail.height,
        size: 'thumbnail',
      };
    }

    const imageId = image.id;
    if (!imageId) {
      throw new Error('Image ID is required');
    }

    const lookupSize = (img: MediaImage): MediaImageSizeContent | undefined => {
      // Check if the requested size exists in the sizes array
      if (!img.sizes || img.sizes.length === 0) {
        return undefined;
      }

      // For custom sizes, check width and height match (when cropping)
      if (size === 'custom' && customSize?.sizing === Sizing.Crop) {
        return img.sizes.find(
          (s) => s.width === customSize.width && s.height === customSize.height
        );
      }

      // For custom sizes where the image is not cropped, check width and height fits within the custom size
      if (size === 'custom' && customSize?.sizing === Sizing.Contain) {
        return img.sizes.find(
          (s) =>
            (s.width === customSize.width && s.height <= customSize.height) ||
            (s.width <= customSize.width && s.height === customSize.height)
        );
      }

      // For preset sizes, check the size name matches
      return img.sizes.find((s) => s.size === size);
    };

    const existingSize = lookupSize(image);
    if (existingSize) {
      console.log(
        `image size '${existingSize.size}' already exists, returning it`
      );
      return existingSize;
    }

    const request = await buildEnsureImageSizeRequest(
      image,
      { size, customSize },
      this.session
    );
    const response = await fetch(request);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to ensure size:', response.status, errorText);
      throw new Error(
        `Cannot set image size: ${response.status} ${response.statusText}`
      );
    }

    // The server will generate the image size before returning the response
    const responseContent = (await response.json()) as unknown;
    console.log(`response from server`, responseContent);

    // Wait for the image record to have the size in its sizes array
    await this._waitFor(imageId, (img: MediaImage) => {
      console.log(`waiting for image to have size`);
      const sizeExists = lookupSize(img);
      if (sizeExists?.url) {
        console.log(`size exists, returning true`);
        return true;
      }
      console.log(`size does not exist, returning false`);
      return false;
    });

    // Return the size content
    return lookupSize(image);
  }

  /**
   * Waits for the media-image record to match the given test.
   * Polls the server by POLL_DELAY until the test passes or MAX_RETRIES is reached.
   * @param id The ID of the media-image record.
   * @param test Function to test the media-image record
   * @returns Promise which resolves when the test passes, or rejects after MAX_RETRIES
   */
  private async _waitFor(
    id: string,
    test: (img: MediaImage) => boolean
  ): Promise<MediaImage> {
    let retries = 0;

    return new Promise((resolve, reject) => {
      const timeout = setInterval(() => {
        const request = findRecord<MediaImage>(DataTypeContent.MediaImage, id, {
          ...jsonApiOptions,
          reload: true,
        } as FindRecordOptions);

        void this.store
          .request(request)
          .then((response) => {
            const image = response.content.data;

            if (test(image)) {
              clearInterval(timeout);
              resolve(image);
            }

            retries++;
            if (retries > MAX_RETRIES) {
              clearInterval(timeout);
              reject(new Error('Timeout waiting for image processing'));
            }
          })
          .catch((error) => {
            // Log but continue retrying on errors
            console.error('Error polling for image:', error);
            retries++;
            if (retries > MAX_RETRIES) {
              clearInterval(timeout);
              reject(new Error('Timeout waiting for image processing'));
            }
          });
      }, POLL_DELAY);
    });
  }

  /**
   * Waits for the thumbnail URL to be available on the media-image record.
   * This is called after uploading a new image to wait for server processing.
   * @param id The ID of the media-image record.
   * @returns Promise which resolves when thumbnailUrl is available
   */
  async waitForThumbnail(id: string): Promise<MediaImage> {
    return this._waitFor(id, (img) => !!img.thumbnailUrl);
  }
}
